import {useUpdateKpis} from '@domain/shifthandover'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {useExpandedGroups} from '@hconnect/common/hooks/useExpandGroups'
import {useProcessStages} from '@hconnect/common/hooks/useProcessStages'
import {ManualKpiToUpdate, ManualKpiResolution, ProcessStage, Shift} from '@hconnect/common/types'
import {getTranslationKey} from '@hconnect/common/utils/translation.utils'
import {ExpandCollapseButton} from '@hconnect/uikit/src/lib2'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnSuccessToast} from '@hm/fundamental'
import {usePlantPermission} from '@hproduce/permissions'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined'
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined'
import {Box, Button, Link, Typography} from '@mui/material'
import React, {useCallback, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {PerformanceManualKpiContainer} from '../../containers'
import {useFilterDatePicker} from '../../hooks'
import {KpiFormData} from '../../types'
import {mapFormDataToKpisToUpdate, mapProcessStage} from '../../utils'

type ManualKpisProps = {
  shifts: Shift[]
  // Injected per app (cockpit legacy dashboard URL vs Suite buildFeatureUrl) — principle 5.
  dashboardUrl: string
}

export const ManualKpis: React.FC<ManualKpisProps> = ({shifts, dashboardUrl}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantId} = useUpmPlantContext()
  const {timeRange, isSingleShift, quickSelectSlot} = useFilterDatePicker(shifts)
  const [isEditMode, setIsEditMode] = useState(false)

  const isManualKpiEditAllowed = usePlantPermission('CREATE_SHIFT_REPORT')

  const isTimeFrameSupported = isSingleShift || quickSelectSlot === 'yesterday'
  const {
    data: processStages = [],
    isLoading: isProcessStagesLoading,
    refetch: refetchProcessStages
  } = useProcessStages<ProcessStage[]>(plantId, {
    enabled: isTimeFrameSupported,
    select: mapProcessStage
  })

  const [openProcessStages, setOpenProcessStages] = useExpandedGroups(
    processStages.map(({code}) => code),
    'expanded-manual-kpis'
  )

  const {control, handleSubmit} = useForm<KpiFormData>({shouldUnregister: true})

  const [manualKpiList, setManualKpiList] = useState<ManualKpiToUpdate[]>([])

  const {
    mutate: updateKpis,
    error: updateError,
    reset: updateReset
  } = useUpdateKpis({
    plantId,
    resolution: isSingleShift ? ManualKpiResolution.PerShift : ManualKpiResolution.Daily,
    timestamp: timeRange.endDate,
    options: {
      onSuccess: () => {
        spawnSuccessToast(t('performance.action.kpiSaveSuccess'))
        setIsEditMode(false)
      }
    }
  })

  const onSubmit = useCallback(
    (data: KpiFormData) => {
      const updatedKpis = mapFormDataToKpisToUpdate(data, manualKpiList)
      updateKpis(updatedKpis)
    },
    [manualKpiList, updateKpis]
  )

  return (
    <ContentBox
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          {t('performance.label.performance')}
          {isTimeFrameSupported && (
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {isEditMode ? (
                <>
                  <Button
                    onClick={() => setIsEditMode(false)}
                    startIcon={<CloseOutlinedIcon />}
                    variant="text"
                    data-test-id="manual-kpis-close-button"
                  >
                    {t('action.cancel')}
                  </Button>
                  <Button
                    onClick={() => void handleSubmit(onSubmit)()}
                    startIcon={<DoneOutlinedIcon />}
                    variant="contained"
                    data-test-id="manual-kpis-save-button"
                  >
                    {t('action.save')}
                  </Button>
                </>
              ) : (
                <>
                  <ExpandCollapseButton
                    variant="text"
                    expanded={openProcessStages.length === processStages.length}
                    onClick={() => setOpenProcessStages('all')}
                    data-test-id="performance-expand-all-button"
                    expandLabel={t('action.expand')}
                    collapseLabel={t('action.collapse')}
                  />
                  {isManualKpiEditAllowed && (
                    <Button
                      onClick={() => setIsEditMode(true)}
                      startIcon={<ModeEditOutlineOutlinedIcon />}
                      variant="text"
                      data-test-id="manual-kpis-edit-button"
                    >
                      {t('summary.action.edit')}
                    </Button>
                  )}
                </>
              )}
              {!isEditMode && (
                <Link
                  sx={{textDecoration: 'none'}}
                  href={dashboardUrl}
                  data-test-id="dashboard-link"
                >
                  <Button
                    variant="text"
                    startIcon={<DashboardIcon />}
                    sx={{width: '200px', height: '58px'}}
                  >
                    {t('goToDashboard')}
                  </Button>
                </Link>
              )}
            </Box>
          )}
        </Box>
      }
      mode="contentDependentHeight"
      bodyWithPadding
      isLoading={isProcessStagesLoading}
      minHeight={isTimeFrameSupported ? '450px' : undefined}
      data-test-id="manual-kpis"
    >
      {isTimeFrameSupported ? (
        <>
          <PerformanceManualKpiContainer
            isEditMode={isEditMode}
            isEnabled={isTimeFrameSupported}
            isProcessStagesLoading={isProcessStagesLoading}
            openProcessStages={openProcessStages}
            setOpenProcessStages={setOpenProcessStages}
            processStages={processStages}
            manualKpiList={manualKpiList}
            setManualKpiList={setManualKpiList}
            control={control}
            updateError={updateError}
            updateReset={updateReset}
            refetchProcessStages={refetchProcessStages}
            isSingleShift={isSingleShift}
            timeRange={timeRange}
          />
        </>
      ) : (
        <Typography data-test-id="manual-kpis-unavailable-label">
          {t(getTranslationKey('performance.label.unavailable'))}
        </Typography>
      )}
    </ContentBox>
  )
}
